import React from "react";
import { useSelector } from "react-redux";
import useISPowner from "../../../hooks/useISPOwner";

const BandwidthInvoice = React.forwardRef(({ invoieData }, ref) => {
  const data = invoieData?.[0];

  // Current User
  const { currentUser } = useISPowner();

  // Get Package
  const packages = useSelector((state) => state?.reseller?.bandwidthPackage);

  //Get Reseller
  const resellers = useSelector((state) => state?.reseller?.bandwidthReseller);

  if (!data) return null;

  //Find Package by Id
  const getPackageName = (id) =>
    packages?.find((p) => p?._id === id)?.name || "N/A";

  //Find Reseller info By Id
  const getResellerName = (id) => {
    return resellers?.find((r) => r?._id === id)?.name || "N/A";
  };

  // Current ressler info
  const resellerInfo = resellers.find((r) => r?._id === data?.reseller);

  const styles = {
    page: {
      maxWidth: "900px",
      margin: "0 auto",
      paddingBottom: "80px",
      minHeight: "100vh",
      backgroundColor: "#fff",
      fontFamily: "Arial, sans-serif",
      color: "#333",
      boxSizing: "border-box",
      position: "relative",
    },

    /* ===== Payment Status Watermark ===== */
    statusWatermark: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) rotate(-45deg)",
      fontSize: "150px",
      fontWeight: "700",
      letterSpacing: "4px",
      opacity: 0.15,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 10,
    },

    statusPaid: {
      color: "#14FA04",
    },

    statusUnpaid: {
      color: "#EC5D71",
    },

    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid #ddd",
      paddingBottom: "20px",
      position: "relative",
      zIndex: 2,
      backgroundColor: "#fff",
    },

    logo: {
      fontSize: "22px",
      fontWeight: "bold",
      color: "#096bce",
    },

    smallText: {
      fontSize: "13px",
      color: "#666",
    },

    infoBox: {
      marginTop: "30px",
      padding: "20px",
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: "#F1F0F0",
      borderRadius: "5px",
      position: "relative",
      zIndex: 2,
    },

    sectionTitle: {
      fontWeight: "bold",
      marginBottom: "6px",
    },

    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "30px",
      backgroundColor: "#fff",
      position: "relative",
      zIndex: 2,
    },

    th: {
      padding: "10px",
      borderBottom: "2px solid #000",
      textAlign: "left",
      backgroundColor: "#fff",
    },

    td: {
      padding: "10px",
      borderBottom: "1px solid #ddd",
      backgroundColor: "#fff",
    },

    summaryWrapper: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: "30px",
      backgroundColor: "#fff",
      position: "relative",
      zIndex: 2,
    },

    summary: {
      width: "300px",
    },

    summaryRow: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: "8px",
    },

    summaryBold: {
      borderTop: "1px solid #000",
      paddingTop: "10px",
      fontWeight: "bold",
    },

    footer: {
      marginTop: "60px",
      display: "flex",
      justifyContent: "space-between",
      fontSize: "13px",
      backgroundColor: "#fff",
      position: "relative",
      zIndex: 2,
    },

    paidBadge: {
      color: "white",
      fontWeight: "500",
      backgroundColor: "#11c205",
      padding: "2px 6px",
      borderRadius: "5px",
      fontSize: "10px",
    },

    unpaidBadge: {
      color: "white",
      fontWeight: "500",
      backgroundColor: "#EC5D71",
      padding: "2px 6px",
      borderRadius: "5px",
      fontSize: "10px",
    },

    poweredBy: {
      position: "absolute",
      bottom: "2px",
      right: "2px",
    },
  };

  return (
    <div ref={ref} style={styles.page}>
      {/* ===== WATERMARK ===== */}
      <div
        style={{
          ...styles.statusWatermark,
          ...(data.paymentStatus === "paid"
            ? styles.statusPaid
            : styles.statusUnpaid),
        }}
      >
        {data.paymentStatus.toUpperCase()}
      </div>

      {/* ===== Header ===== */}
      <div style={styles.header}>
        <div>
          <div style={styles.logo}>{currentUser?.ispOwner?.company} </div>
          <div style={styles.smallText}>Bandwidth Service Provider</div>
          <div style={styles.smallText}>
            Email: {currentUser?.ispOwner?.email}{" "}
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <h1 style={{ margin: 0 }}>INVOICE</h1>
          <div style={styles.smallText}>
            Date: {new Date(data.createdAt).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* ===== Info ===== */}
      <div style={styles.infoBox}>
        <div>
          <div style={styles.sectionTitle}>Invoice To</div>
          <div>Name: {getResellerName(data.reseller)}</div>
          {resellerInfo.mobile && <div>Mobile: {resellerInfo.mobile}</div>}
          {resellerInfo.email && <div>Email: {resellerInfo.email}</div>}
        </div>

        <div>
          <div style={styles.sectionTitle}>Invoice Info</div>
          <div>ID: {data._id}</div>
          <div>Month: {data.month}</div>
          <div>
            Status:{" "}
            <span
              style={
                data.paymentStatus === "paid"
                  ? styles.paidBadge
                  : styles.unpaidBadge
              }
            >
              {data.paymentStatus.toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* ===== Table ===== */}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Package</th>
            <th style={styles.th}>Start to End Date</th>
            <th style={styles.th}>Rate</th>
            <th style={styles.th}>Qty</th>
            <th style={styles.th}>Total</th>
          </tr>
        </thead>
        <tbody>
          {data.bandwidth?.map((item, i) => (
            <tr key={i}>
              <td style={styles.td}>{getPackageName(item.package)}</td>
              <td style={styles.td}>
                {new Date(item.startDate).toLocaleDateString()} to{" "}
                {new Date(item.endDate).toLocaleDateString()}
              </td>
              <td style={styles.td}>{item.price} ৳</td>
              <td style={styles.td}>{item.quantity}</td>
              <td style={styles.td}>{item.total} ৳</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ===== Summary ===== */}
      <div style={styles.summaryWrapper}>
        <div style={styles.summary}>
          <div style={styles.summaryRow}>
            <span>Previous Due</span>
            <span>{data.previousDue} ৳</span>
          </div>

          <div style={styles.summaryRow}>
            <span>Due Amount</span>
            <span>{data.dueAmount} ৳</span>
          </div>
          <div style={styles.summaryRow}>
            <span>Total Amount</span>
            <span>{data.totalAmount} ৳</span>
          </div>
          <div style={{ ...styles.summaryRow, ...styles.summaryBold }}>
            <span>Grand Total</span>
            <span>{data.grandTotal} ৳</span>
          </div>
        </div>
      </div>

      {/* ===== Footer ===== */}
      <div style={styles.footer}>
        <div>
          <div style={{ marginTop: "50px" }}>____________________</div>
          <b style={{ paddingLeft: "5px" }}>Signature</b>
        </div>
      </div>

      <div style={styles.poweredBy}>
        <p>
          Powered By{" "}
          <span style={{ fontWeight: "600" }}>
            {" "}
            <span className="text-primary">Net</span>Fee –
          </span>{" "}
          An ISP Billing Software
        </p>
      </div>
    </div>
  );
});

export default BandwidthInvoice;
